import {
  getCampaignRunsQueryOptions,
  useSimpleCampaigns,
} from "@/api/campaign.api";
import { createLeadsQueryOptions, ListLeadsParams } from "@/api/leads.api";
import { Lead } from "@/types/campaign";
import { queryClient } from "@/utils/api";
import {
  DataTag,
  hashKey,
  notifyManager,
  Query,
  QueryKey,
} from "@tanstack/react-query";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  LassoPushMessageListener,
  usePushMessageListener,
} from "./socket.service";

type InferQuery<T extends { queryKey: readonly unknown[] }> = T extends {
  queryKey: DataTag<infer _TType, infer TValue, infer TError>;
}
  ? Query<TValue, TError, TValue, T["queryKey"]>
  : never;

type LeadsQuery = InferQuery<ReturnType<typeof createLeadsQueryOptions>>;

const cleanupFns: (() => void)[] = [];

function createMapStore<K, V>(initialState: Map<K, V> = new Map()) {
  let state = initialState;

  const subscribers = new Set<() => void>();

  const notify = () => {
    for (const callback of subscribers) {
      callback();
    }
  };

  const getState = () => state;

  const setState = (
    stateOrCallback: Map<K, V> | ((prevState: Map<K, V>) => Map<K, V>),
  ) => {
    state =
      typeof stateOrCallback === "function"
        ? stateOrCallback(state)
        : stateOrCallback;

    notify();
  };

  const subscribe = (callback: () => void) => {
    subscribers.add(callback);
    return () => {
      subscribers.delete(callback);
    };
  };

  const useStore = () => useSyncExternalStore(subscribe, getState);

  return {
    getState,
    subscribe,
    useStore,

    get: (key: K) => state.get(key),

    set: (key: K, callback: (prevValue: V | undefined) => V) => {
      setState((prevState) => {
        const newState = new Map(prevState);
        newState.set(key, callback(newState.get(key)));
        return newState;
      });
    },

    delete: (key: K) => {
      setState((prevState) => {
        const newState = new Map(prevState);
        newState.delete(key);
        return newState;
      });
    },

    clear: () => setState(new Map()),

    has: (key: K) => state.has(key),
    entries: () => state.entries(),
    keys: () => state.keys(),
    values: () => state.values(),
    [Symbol.iterator]: () => state[Symbol.iterator](),
  };
}

const store = createMapStore<
  string,
  { leadsQuery: LeadsQuery; pushMessageLeads: Lead[] }
>();

const isLeadsQuery = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query: Query<any, any, any, any>,
): query is LeadsQuery => {
  if (!Array.isArray(query.queryKey)) return false;
  const [first = null] = query.queryKey;
  return first === "leads";
};

// Subscribe to query cache changes to keep track of all leads queries.
{
  const unsubscribe = queryClient.getQueryCache().subscribe(
    notifyManager.batchCalls((event) => {
      switch (event.type) {
        case "added":
        case "updated":
        case "removed":
          break;

        default:
          return;
      }

      const query: Query = event.query;

      if (!isLeadsQuery(query)) return;

      switch (event.type) {
        case "added":
        case "updated": {
          store.set(query.queryHash, (prev) => ({
            leadsQuery: query,
            pushMessageLeads: prev?.pushMessageLeads ?? [],
          }));
          break;
        }

        case "removed": {
          store.delete(query.queryHash);
          break;
        }
      }
    }),
  );

  cleanupFns.push(unsubscribe);
}

// This should only be used on the root route.
export function usePushMessageLeadsService() {
  const simpleCampaigns = useSimpleCampaigns();

  const campaignsById = useMemo(
    () => new Map(simpleCampaigns?.map((campaign) => [campaign.id, campaign])),
    [simpleCampaigns],
  );

  const leadMatchesParams = useCallback(
    (lead: Lead, params: ListLeadsParams): boolean => {
      const campaign = campaignsById.get(lead.campaignId);
      if (!campaign) return false;

      // If name filter is applied, do not show push messages.
      if (params.name) return false;

      if (params.campaignId?.length) {
        const campaignIds = new Set(params.campaignId);
        if (!campaignIds.has(lead.campaignId)) return false;
      }

      if (params.status?.length) {
        const statuses = new Set(params.status);
        if (!statuses.has(lead.status)) return false;
      }

      if (params.configurationName?.length) {
        const configuration = campaign.configurations.find(
          (c) => c.id === lead.configurationId,
        );
        if (!configuration?.name) return false;

        const configurationNames = new Set(params.configurationName);
        if (!configurationNames.has(configuration.name)) return false;
      }

      return true;
    },
    [campaignsById],
  );

  usePushMessageListener(
    "LeadDelivered",
    useCallback<LassoPushMessageListener>(
      ({ message }) => {
        const lead = message as Lead;

        for (const [queryHash, { leadsQuery, pushMessageLeads }] of store) {
          const [, params] = leadsQuery.queryKey;

          if (!leadMatchesParams(lead, params)) continue;

          const hasQueryLeads = !!leadsQuery.state.data?.pages?.find(
            (page) => page.results.length,
          );

          store.set(queryHash, () => ({
            leadsQuery,
            pushMessageLeads: hasQueryLeads
              ? [lead, ...pushMessageLeads]
              : [...pushMessageLeads, lead],
          }));
        }

        // Update the campaign runs cache if needed.
        queryClient.setQueryData(
          getCampaignRunsQueryOptions(lead.campaignId).queryKey,
          (prev) => {
            if (lead.runId !== prev?.latestRun?.id) return prev;
            return {
              ...prev,
              latestRun: {
                ...prev.latestRun,
                delivered: (prev.latestRun.delivered ?? 0) + 1,
              },
            };
          },
        );
      },
      [leadMatchesParams],
    ),
  );

  usePushMessageListener(
    "InsufficientFundsForLead",
    useCallback<LassoPushMessageListener>(({ message }) => {
      const lead = message as Lead;

      // Update the campaign runs cache if needed.
      queryClient.setQueryData(
        getCampaignRunsQueryOptions(lead.campaignId).queryKey,
        (prev) => {
          if (lead.runId !== prev?.latestRun?.id) return prev;
          return {
            ...prev,
            latestRun: {
              ...prev.latestRun,
              insufficientFunds: (prev.latestRun.insufficientFunds ?? 0) + 1,
            },
          };
        },
      );
    }, []),
  );
}

export function usePushMessageLeads(queryKey: QueryKey) {
  const state = store.useStore();

  const queryHash = useMemo(() => hashKey(queryKey), [queryKey]);

  return useMemo(
    () => state.get(queryHash)?.pushMessageLeads ?? [],
    [state, queryHash],
  );
}

export function getPushMessageLeads(queryKey: QueryKey) {
  const state = store.getState();
  return state.get(hashKey(queryKey))?.pushMessageLeads ?? [];
}

// setTimeout(async () => {
//   for (let i = 0; i < 10; i++) {
//     await new Promise((resolve) => setTimeout(resolve, 1000));

//     const fakeLead: Lead = {
//       id: 2000000 + i,
//       campaignId: 565,
//       configurationId: 1184,
//       // configurationHistoryId: 771,
//       lassoId: "CVR-1-35450645",
//       thirdPartyId: null,
//       // thirdPartySystem: null,
//       updated: "2025-10-14T12:06:58.5621391Z",
//       created: "2025-10-14T12:06:45.8426294Z",
//       status: "New",
//       // deliveryStatus: "Delivered",
//       statusReason: null,
//       followUpTime: null,
//       // deliveryError: null,
//       feedback: null,
//       contact: null,
//       events: null,
//       runId: 1108,
//       // phoneNumberPurchased: false,
//       // phoneNumberPurchaseDate: null,
//       creditsUsed: 1,
//       // creditsUsedOnPhone: 0,
//       name: `Fake Lead #${i + 1}`,
//     };

//     for (const [queryHash, { leadsQuery, pushMessageLeads }] of store) {
//       const [, params] = leadsQuery.queryKey;

//       if (params.campaignId?.length) {
//         if (!params.campaignId.includes(fakeLead.campaignId)) continue;
//       }

//       console.log("Injecting fake lead into query", i + 1, {
//         queryHash,
//         fakeLead,
//       });
//       store.set(queryHash, () => ({
//         leadsQuery,
//         pushMessageLeads: [fakeLead, ...pushMessageLeads],
//       }));
//     }
//   }
// }, 2000);

if (import.meta.hot) {
  import.meta.hot.accept(() => {
    import.meta.hot?.invalidate();
  });

  import.meta.hot.dispose(() => {
    for (const fn of cleanupFns) {
      fn();
    }
  });
}
